(() => {
  const galleries = new WeakMap();
  const forms = new WeakSet();
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mediaKey = value => String(value || '').split('/').pop();
  const elements = (root, selector) => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];

  function initializeGallery(gallery) {
    if (galleries.has(gallery)) return;
    const track = gallery.querySelector('[data-at-track]');
    const slides = [...gallery.querySelectorAll('[data-at-slide]')];
    const buttons = [...gallery.querySelectorAll('[data-at-slide-to]')];
    if (!track || !slides.length) return;
    let current = 0;
    let frame = 0;

    const hydrate = index => {
      const slide = slides[index];
      const template = slide?.querySelector('template');
      if (template) {
        template.replaceWith(template.content.cloneNode(true));
      }
    };
    const pause = slide => {
      slide.querySelectorAll('video').forEach(video => video.pause());
      slide.querySelectorAll('iframe').forEach(iframe => {
        if (/youtube/.test(iframe.src)) iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
        if (/vimeo/.test(iframe.src)) iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
      });
    };
    const update = index => {
      current = Math.max(0, Math.min(slides.length - 1, index));
      gallery.dataset.activeIndex = String(current);
      [current - 1, current, current + 1].forEach(hydrate);
      slides.forEach((slide, i) => {
        slide.dataset.active = String(i === current);
        if (i !== current) pause(slide);
      });
      buttons.forEach(button => {
        const active = Number(button.dataset.atSlideTo) === current;
        button.setAttribute('aria-pressed', String(active));
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      gallery.querySelectorAll('[data-at-gallery-count]').forEach(counter => {
        counter.textContent = `${current + 1} / ${slides.length}`;
      });
      gallery.querySelectorAll('[data-at-gallery-step]').forEach(button => {
        const step = Number(button.dataset.atGalleryStep);
        button.disabled = (step < 0 && current === 0) || (step > 0 && current === slides.length - 1);
      });
      const thumbnail = buttons.find(button => Number(button.dataset.atSlideTo) === current);
      const strip = thumbnail?.parentElement;
      if (strip && strip.scrollWidth > strip.clientWidth) {
        const stripRect = strip.getBoundingClientRect();
        const buttonRect = thumbnail.getBoundingClientRect();
        if (buttonRect.left < stripRect.left || buttonRect.right > stripRect.right) {
          strip.scrollTo({ left: strip.scrollLeft + buttonRect.left - stripRect.left - (strip.clientWidth - buttonRect.width) / 2, behavior: reduceMotion() ? 'auto' : 'smooth' });
        }
      }
    };
    const go = (index, behavior = reduceMotion() ? 'auto' : 'smooth') => {
      const next = Math.max(0, Math.min(slides.length - 1, index));
      update(next);
      const target = slides[next].getBoundingClientRect();
      const viewport = track.getBoundingClientRect();
      track.scrollTo({ left: track.scrollLeft + target.left - viewport.left, behavior });
    };
    const nearest = () => {
      const center = track.getBoundingClientRect().left + track.clientWidth / 2;
      let distance = Infinity;
      let index = current;
      slides.forEach((slide, i) => {
        const rect = slide.getBoundingClientRect();
        const delta = Math.abs(rect.left + rect.width / 2 - center);
        if (delta < distance) { distance = delta; index = i; }
      });
      if (index !== current) update(index);
    };
    track.addEventListener('scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; nearest(); });
    }, { passive: true });
    track.addEventListener('scrollend', nearest);
    buttons.forEach(button => button.addEventListener('click', () => go(Number(button.dataset.atSlideTo))));
    gallery.querySelectorAll('[data-at-gallery-step]').forEach(button => button.addEventListener('click', () => go(current + Number(button.dataset.atGalleryStep))));
    gallery.addEventListener('keydown', event => {
      if (!event.target.closest('[data-at-slide-to], [data-at-track]') || event.target.matches('input,textarea,select,video,iframe')) return;
      const keyIndex = { ArrowLeft: current - 1, ArrowRight: current + 1, Home: 0, End: slides.length - 1 }[event.key];
      if (keyIndex === undefined) return;
      event.preventDefault();
      go(keyIndex);
      if (event.target.matches('[data-at-slide-to]')) buttons.find(button => Number(button.dataset.atSlideTo) === current)?.focus({ preventScroll: true });
    });
    const api = {
      showMedia(id) {
        const index = slides.findIndex(slide => mediaKey(slide.dataset.mediaId) === mediaKey(id));
        if (id && index !== -1) go(index);
      },
      activeSlide: () => slides[current],
      go
    };
    galleries.set(gallery, api);
    update(0);
    if ('ResizeObserver' in window) new ResizeObserver(() => go(current, 'auto')).observe(track);
  }

  function initializeProduct(form) {
    if (forms.has(form)) return;
    const section = form.closest('[data-at-product]');
    const quantity = form.querySelector('[name="quantity"]');
    const add = form.querySelector('[name="add"]');
    const variantsNode = section?.querySelector('[data-at-variants]');
    if (!section || !quantity || !add || !variantsNode) return;
    let variants;
    try { variants = JSON.parse(variantsNode.textContent); } catch { return; }
    if (!variants.length) return;
    forms.add(form);
    const message = section.querySelector('[data-at-message]');
    let submitting = false;
    const selected = () => variants.find(variant => String(variant.id) === String(new FormData(form).get('id'))) || variants[0];
    const status = text => { if (message) message.textContent = text; };
    const label = text => { const node = add.querySelector('[data-at-add-label]'); if (node) node.textContent = text; };
    const normalizeQuantity = () => {
      const minimum = Math.max(1, Number(quantity.min) || 1);
      const maximum = Number(quantity.max) || 9999;
      quantity.value = String(Math.max(minimum, Math.min(maximum, parseInt(quantity.value, 10) || minimum)));
      form.querySelectorAll('[data-at-qty]').forEach(button => {
        const step = Number(button.dataset.atQty);
        button.disabled = (step < 0 && Number(quantity.value) <= minimum) || (step > 0 && Number(quantity.value) >= maximum);
      });
    };
    const synchronize = (updateMedia = false) => {
      const variant = selected();
      document.querySelectorAll('[data-at-price]').forEach(node => { node.textContent = variant.priceText; });
      section.querySelectorAll('[data-at-compare]').forEach(node => { node.textContent = variant.compareText || ''; node.hidden = !variant.onSale; });
      section.querySelectorAll('[data-at-save]').forEach(node => { node.textContent = `${section.dataset.saveLabel || ''} ${variant.saveText || ''}`.trim(); node.hidden = !variant.onSale; });
      add.disabled = !variant.available || submitting;
      label(submitting ? section.dataset.addingLabel : variant.available ? section.dataset.addLabel : section.dataset.soldLabel);
      document.querySelectorAll('[data-at-sticky-add]').forEach(button => { button.disabled = !variant.available || submitting; });
      if (updateMedia) section.querySelectorAll('[data-at-gallery]').forEach(gallery => galleries.get(gallery)?.showMedia(variant.mediaId));
      section.dispatchEvent(new CustomEvent('at:variant:change', { bubbles: true, detail: { variant } }));
    };
    form.querySelectorAll('[name="id"]').forEach(input => input.addEventListener('change', () => {
      synchronize(true);
      const url = new URL(location.href);
      url.searchParams.set('variant', selected().id);
      history.replaceState({}, '', url);
    }));
    form.querySelectorAll('[data-at-qty]').forEach(button => button.addEventListener('click', () => {
      quantity.value = String((parseInt(quantity.value, 10) || 1) + Number(button.dataset.atQty));
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    }));
    quantity.addEventListener('change', normalizeQuantity);
    normalizeQuantity();
    synchronize(Boolean(new URL(location.href).searchParams.get('variant')));

    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (submitting || !selected().available) return;
      normalizeQuantity();
      submitting = true;
      synchronize();
      add.setAttribute('aria-busy', 'true');
      status('');
      const drawer = document.querySelector('cart-drawer');
      const data = new FormData(form);
      const sections = drawer?.getSectionsToRender?.();
      if (sections?.length) data.set('sections', sections.map(item => item.id).join(','));
      data.set('sections_url', location.pathname);
      drawer?.setActiveElement?.(add);
      let added = false;
      try {
        const path = window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add`;
        const response = await fetch(path.endsWith('.js') ? path : `${path}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: data });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || section.dataset.errorLabel);
        added = true;
        status(section.dataset.addedLabel);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'vf-atlas', cartData: result } }));
        if (drawer?.renderContents) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(result);
        } else {
          location.href = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
        }
      } catch (error) {
        status(added ? section.dataset.addedFallback : error.message || section.dataset.errorLabel);
      } finally {
        submitting = false;
        add.removeAttribute('aria-busy');
        synchronize();
      }
    });
  }

  function initializeTabs(group) {
    if (group.dataset.tabsReady) return;
    const tabs = [...group.querySelectorAll('[role="tab"]')];
    const panels = [...group.querySelectorAll('[role="tabpanel"]')];
    if (!tabs.length) return;
    group.dataset.tabsReady = 'true';
    let current = 0;
    const show = (index, focus = false) => {
      current = (index + tabs.length) % tabs.length;
      tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === current)); tab.tabIndex = i === current ? 0 : -1; });
      panels.forEach((panel, i) => { panel.hidden = i !== current; });
      group.querySelectorAll('[data-at-tab-image]').forEach((image, i) => { image.hidden = i !== current; });
      group.querySelectorAll('[data-at-tab-count]').forEach(count => { count.textContent = `${current + 1} / ${tabs.length}`; });
      if (focus) tabs[current].focus();
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => show(i));
      tab.addEventListener('keydown', event => {
        const index = { ArrowRight: current + 1, ArrowLeft: current - 1, Home: 0, End: tabs.length - 1 }[event.key];
        if (index === undefined) return;
        event.preventDefault();
        show(index, true);
      });
    });
    group.querySelectorAll('[data-at-tab-step]').forEach(button => button.addEventListener('click', () => show(current + Number(button.dataset.atTabStep))));
    show(0);
  }

  function initialize(root = document) {
    elements(root, '[data-at-gallery]').forEach(initializeGallery);
    elements(root, '[data-at-form]').forEach(initializeProduct);
    elements(root, '[data-at-tabs]').forEach(initializeTabs);
    elements(root, '[data-at-delivery]').forEach(element => {
      const minimum = Number(element.dataset.min);
      const maximum = Number(element.dataset.max);
      if (!(minimum > 0 && maximum >= minimum)) return;
      const businessDate = days => {
        const date = new Date();
        date.setHours(12, 0, 0, 0);
        for (let elapsed = 0; elapsed < days;) {
          date.setDate(date.getDate() + 1);
          if (date.getDay() !== 0 && date.getDay() !== 6) elapsed++;
        }
        return date;
      };
      const format = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: 'short', day: 'numeric' });
      element.textContent = `${format.format(businessDate(minimum))} – ${format.format(businessDate(maximum))}`;
    });
    const reveals = elements(root, '.at-scroll-reveal');
    if ('IntersectionObserver' in window && !reduceMotion()) {
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) { entry.target.classList.add('at-in-view'); observer.unobserve(entry.target); }
      }), { threshold: 0.1 });
      reveals.forEach(element => observer.observe(element));
    } else reveals.forEach(element => element.classList.add('at-in-view'));
    const form = document.querySelector('[data-at-form]');
    const sticky = document.querySelector('[data-at-sticky]');
    if (form && sticky && !sticky.dataset.stickyReady && 'IntersectionObserver' in window) {
      sticky.dataset.stickyReady = 'true';
      new IntersectionObserver(([entry]) => {
        const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('at-visible', visible);
        sticky.inert = !visible;
      }, { threshold: 0 }).observe(form);
    }
  }

  document.addEventListener('click', event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const purchase = target.closest('[data-at-sticky-add]');
    if (purchase) { document.querySelector('[data-at-form]')?.requestSubmit(); return; }
    const bag = target.closest('[data-at-open-bag]');
    if (bag) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.open) { event.preventDefault(); drawer.open(bag); }
      return;
    }
    const zoom = target.closest('[data-at-zoom]');
    if (zoom) {
      const gallery = zoom.closest('[data-at-gallery]');
      const source = gallery ? galleries.get(gallery)?.activeSlide() : zoom.closest('[data-at-zoom-scope], [data-at-tab-image]');
      const image = source?.querySelector('img');
      const dialog = document.querySelector('[data-at-zoom-dialog]');
      const destination = dialog?.querySelector('img');
      if (!image || !destination || !dialog?.showModal) return;
      const url = new URL(image.dataset.full || image.dataset.zoom || image.currentSrc || image.src, location.href);
      url.searchParams.set('width', '2048');
      destination.src = url.href;
      destination.alt = image.alt;
      const viewport = dialog.querySelector('[data-at-zoom-viewport]');
      if (viewport) { viewport.dataset.zoomed = 'false'; viewport.scrollTop = 0; viewport.scrollLeft = 0; }
      dialog.querySelector('[data-at-zoom-toggle]')?.setAttribute('aria-pressed', 'false');
      dialog.showModal();
      return;
    }
    const toggle = target.closest('[data-at-zoom-toggle]');
    if (toggle) {
      const viewport = toggle.closest('dialog')?.querySelector('[data-at-zoom-viewport]');
      if (viewport) { const zoomed = viewport.dataset.zoomed !== 'true'; viewport.dataset.zoomed = String(zoomed); toggle.setAttribute('aria-pressed', String(zoomed)); }
      return;
    }
    const policy = target.closest('[data-at-policy]');
    if (policy) { event.preventDefault(); document.getElementById(policy.dataset.atPolicy)?.showModal(); return; }
    const close = target.closest('[data-at-close]');
    if (close) { close.closest('dialog')?.close(); return; }
    if (target.matches('dialog')) {
      const rect = target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) target.close();
    }
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
})();
